import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Loader2, Eye, Copy, ExternalLink,
  Save, Send, Info, ChevronDown, ChevronUp, Sparkles, Upload, LayoutTemplate, FileUp,
  AlertTriangle, Download, Wand2, ShieldCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import QRStudio from "@/components/QRStudio";
import { CITATION_STYLES, formatReference, isLikelyValidDOI, SOURCE_TYPES, resolveSourceType, yearSuffixMap, inTextCitation, isNumbered, numberedInText } from "@/lib/citations";
import { CitationText } from "@/components/CitationText";
import { parseReferences, isStructuredImport } from "@/lib/importRefs";
import { parseDocx, parseReferenceText, dedupKey } from "@/lib/docxImport";
import { extractPdfText } from "@/lib/pdfImport";
import { exportReferences } from "@/lib/exportRefs";
import { normalizeAuthors } from "@/lib/authors";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import api, { errMessage } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { referencePageUrl, referenceQrUrl } from "@/lib/publicUrl";
import { ShareMenu } from "@/components/ShareMenu";

const newRef = () => ({
  id: (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()),
  source_type: "journal", work_type: "doctoral", conf_type: "paper", authors: "", authors_raw: "", editors: "", editors_raw: "", edition: "", container: "", institution: "", conference: "", location: "", event_date: "",
  year: "", month: "", day: "", title: "", source: "", volume: "", issue: "",
  pages: "", publisher: "", report_number: "", doi: "", url: "", accessed: "", note: "",
});

function RefCard({ index, total, item, onChange, onMove, onDelete, style, yearSuffix }) {
  const [open, setOpen] = useState(true);
  const [doiQuery, setDoiQuery] = useState(item.doi || "");
  const [autofilling, setAutofilling] = useState(false);
  const [pmid, setPmid] = useState("");
  const [pmFilling, setPmFilling] = useState(false);
  const set = (k, v) => onChange({ ...item, [k]: v });
  const preview = formatReference(item, style, yearSuffix);

  const applyFetched = (data, fallbackDoi) => {
    onChange({
      ...item,
      authors: data.authors || item.authors,
      authors_raw: data.authors || item.authors_raw || item.authors,
      year: data.year || item.year,
      title: data.title || item.title,
      source: data.source || item.source,
      volume: data.volume || item.volume,
      issue: data.issue || item.issue,
      pages: data.pages || item.pages,
      publisher: data.publisher || item.publisher,
      doi: data.doi || fallbackDoi || item.doi,
      url: data.url || item.url,
    });
  };

  const autofill = async () => {
    const q = (doiQuery || item.doi || "").trim();
    if (!q) { toast.error("Enter a DOI to autofill (e.g. 10.1038/nphys1170)."); return; }
    setAutofilling(true);
    try {
      const { data } = await api.get("/lookup/doi", { params: { doi: q } });
      applyFetched(data, q);
      toast.success("Fields filled from DOI — please review before publishing.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setAutofilling(false);
    }
  };

  const autofillPubmed = async () => {
    const q = pmid.trim();
    if (!q) { toast.error("Enter a PubMed ID (e.g. 30923678)."); return; }
    setPmFilling(true);
    try {
      const { data } = await api.get("/lookup/pubmed", { params: { pmid: q } });
      applyFetched(data, data.doi);
      toast.success("Fields filled from PubMed — please review before publishing.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setPmFilling(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`reference-card-${index}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left" data-testid={`reference-toggle-${index}`}>
            {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            <span className="text-sm font-semibold text-slate-900">Reference {index + 1}</span>
          </button>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Move up" data-testid={`reference-up-${index}`}><ArrowUp className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" disabled={index === total - 1} onClick={() => onMove(index, 1)} aria-label="Move down" data-testid={`reference-down-${index}`}><ArrowDown className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => onDelete(index)} aria-label="Delete reference" data-testid={`reference-delete-${index}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        </div>

        {open && (
          <>
          <div className="mt-4 rounded-lg border border-teal-200 bg-teal-50/60 p-3">
            <Label htmlFor={`doi-lookup-${item.id}`} className="flex items-center gap-1.5 text-[#0F766E]">
              <Sparkles className="h-3.5 w-3.5" /> Autofill from DOI
            </Label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input
                id={`doi-lookup-${item.id}`}
                placeholder="Paste a DOI to autofill"
                value={doiQuery}
                onChange={(e) => setDoiQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); autofill(); } }}
                data-testid={`doi-lookup-input-${index}`}
                className="bg-white"
              />
              <Button type="button" onClick={autofill} disabled={autofilling} className="shrink-0 bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid={`doi-autofill-btn-${index}`}>
                {autofilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Autofill
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">Fetches title, authors, journal, and year from Crossref. Always review before publishing.</p>
            <div className="mt-3 border-t border-teal-200/70 pt-3">
              <Label htmlFor={`pmid-lookup-${item.id}`} className="flex items-center gap-1.5 text-[#0F766E]">
                <Sparkles className="h-3.5 w-3.5" /> Autofill from PubMed ID
              </Label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input
                  id={`pmid-lookup-${item.id}`}
                  placeholder="Paste a PubMed ID (e.g. 30923678)"
                  value={pmid}
                  onChange={(e) => setPmid(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); autofillPubmed(); } }}
                  data-testid={`pmid-lookup-input-${index}`}
                  className="bg-white"
                />
                <Button type="button" onClick={autofillPubmed} disabled={pmFilling} variant="outline" className="shrink-0 border-[#0D9488] text-[#0F766E] hover:bg-teal-50" data-testid={`pmid-autofill-btn-${index}`}>
                  {pmFilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Autofill
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-slate-500">For medical & life-sciences references, from PubMed (NCBI).</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`stype-${item.id}`}>Source type</Label>
              <Select value={item.source_type || "journal"} onValueChange={(v) => set("source_type", v)}>
                <SelectTrigger id={`stype-${item.id}`} data-testid={`ref-source-type-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`source-type-${s.value}-${index}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`authors-${item.id}`}>Author(s)</Label>
              <Input id={`authors-${item.id}`} placeholder="Enter author(s)" value={item.authors} onChange={(e) => onChange({ ...item, authors: e.target.value, authors_raw: e.target.value })} data-testid={`ref-authors-${index}`} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`year-${item.id}`}>Date <span className="text-slate-400">(year · month · day)</span></Label>
              <div className="grid grid-cols-3 gap-2">
                <Input id={`year-${item.id}`} placeholder="2024" value={item.year} onChange={(e) => set("year", e.target.value)} data-testid={`ref-year-${index}`} />
                <Input placeholder="Month" value={item.month || ""} onChange={(e) => set("month", e.target.value)} data-testid={`ref-month-${index}`} />
                <Input placeholder="Day" value={item.day || ""} onChange={(e) => set("day", e.target.value)} data-testid={`ref-day-${index}`} />
              </div>
            </div>
            <div className="space-y-1.5"><Label htmlFor={`title-${item.id}`}>Title</Label><Input id={`title-${item.id}`} placeholder="Enter title" value={item.title} onChange={(e) => set("title", e.target.value)} data-testid={`ref-title-${index}`} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor={`source-${item.id}`}>{item.source_type === "dissertation" ? "Repository / Database (if published)" : "Journal / Source"}</Label><Input id={`source-${item.id}`} placeholder={item.source_type === "dissertation" ? "e.g. ProQuest Dissertations & Theses" : "Enter journal or source"} value={item.source} onChange={(e) => set("source", e.target.value)} data-testid={`ref-source-${index}`} /></div>
            <div className="space-y-1.5"><Label htmlFor={`vol-${item.id}`}>Volume</Label><Input id={`vol-${item.id}`} value={item.volume} onChange={(e) => set("volume", e.target.value)} data-testid={`ref-volume-${index}`} /></div>
            <div className="space-y-1.5"><Label htmlFor={`iss-${item.id}`}>Issue</Label><Input id={`iss-${item.id}`} value={item.issue} onChange={(e) => set("issue", e.target.value)} data-testid={`ref-issue-${index}`} /></div>
            <div className="space-y-1.5"><Label htmlFor={`pg-${item.id}`}>Pages</Label><Input id={`pg-${item.id}`} placeholder="Enter page range" value={item.pages} onChange={(e) => set("pages", e.target.value)} data-testid={`ref-pages-${index}`} /></div>
            <div className="space-y-1.5"><Label htmlFor={`pub-${item.id}`}>Publisher</Label><Input id={`pub-${item.id}`} value={item.publisher} onChange={(e) => set("publisher", e.target.value)} data-testid={`ref-publisher-${index}`} /></div>
            <div className="space-y-1.5"><Label htmlFor={`doi-${item.id}`}>DOI <span className="text-slate-400">(optional)</span></Label><Input id={`doi-${item.id}`} placeholder="Enter DOI" value={item.doi} onChange={(e) => set("doi", e.target.value)} data-testid={`ref-doi-${index}`} />
              {item.doi && !isLikelyValidDOI(item.doi) && (
                <p className="flex items-center gap-1 text-xs text-amber-700" data-testid={`ref-doi-warning-${index}`}>
                  <AlertTriangle className="h-3 w-3" /> This doesn't look like a valid DOI (expected e.g. 10.1000/xyz).
                </p>
              )}
            </div>
            <div className="space-y-1.5"><Label htmlFor={`url-${item.id}`}>Link URL <span className="text-slate-400">(optional)</span></Label><Input id={`url-${item.id}`} placeholder="Enter link URL" value={item.url} onChange={(e) => set("url", e.target.value)} data-testid={`ref-url-${index}`} /></div>
            {(["website", "report", "other"].includes(item.source_type || "") || (item.url || "").trim()) && (
              <div className="space-y-1.5">
                <Label htmlFor={`accessed-${item.id}`}>Accessed / Retrieved date <span className="text-slate-400">(optional)</span></Label>
                <div className="flex gap-2">
                  <Input id={`accessed-${item.id}`} type="date" value={item.accessed || ""} onChange={(e) => set("accessed", e.target.value)} data-testid={`ref-accessed-${index}`} />
                  <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => set("accessed", new Date().toISOString().slice(0, 10))} data-testid={`ref-accessed-today-${index}`}>Today</Button>
                </div>
                <p className="text-xs text-slate-500">Shown as "Retrieved …" (APA) or "Accessed …" (MLA/Chicago) for online sources.</p>
              </div>
            )}
            {["book", "chapter", "report", "dissertation", "other", "website"].includes(item.source_type || "") && (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`editors-${item.id}`}>Editor(s) <span className="text-slate-400">(optional)</span></Label>
                  <Input id={`editors-${item.id}`} placeholder="e.g. Jane Doe; John Roe" value={item.editors || ""} onChange={(e) => onChange({ ...item, editors: e.target.value, editors_raw: e.target.value })} data-testid={`ref-editors-${index}`} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`edition-${item.id}`}>Edition <span className="text-slate-400">(optional)</span></Label>
                  <Input id={`edition-${item.id}`} placeholder="e.g. 2nd" value={item.edition || ""} onChange={(e) => set("edition", e.target.value)} data-testid={`ref-edition-${index}`} />
                </div>
                {item.source_type === "chapter" && (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor={`container-${item.id}`}>Book / Container title</Label>
                    <Input id={`container-${item.id}`} placeholder="Title of the book this chapter is in" value={item.container || ""} onChange={(e) => set("container", e.target.value)} data-testid={`ref-container-${index}`} />
                  </div>
                )}
              </>
            )}
            {item.source_type === "report" && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor={`report-no-${item.id}`}>Report number <span className="text-slate-400">(optional)</span></Label>
                <Input id={`report-no-${item.id}`} placeholder="e.g. 2021-05" value={item.report_number || ""} onChange={(e) => set("report_number", e.target.value)} data-testid={`ref-report-number-${index}`} />
              </div>
            )}
            {item.source_type === "dissertation" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor={`worktype-${item.id}`}>Work type</Label>
                  <Select value={item.work_type || "doctoral"} onValueChange={(v) => set("work_type", v)}>
                    <SelectTrigger id={`worktype-${item.id}`} data-testid={`ref-work-type-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="doctoral" data-testid={`work-type-doctoral-${index}`}>Doctoral dissertation</SelectItem>
                      <SelectItem value="masters" data-testid={`work-type-masters-${index}`}>Master's thesis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`institution-${item.id}`}>Institution (degree-granting)</Label>
                  <Input id={`institution-${item.id}`} placeholder="e.g. Minnesota State University, Mankato" value={item.institution || ""} onChange={(e) => set("institution", e.target.value)} data-testid={`ref-institution-${index}`} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`pub-no-${item.id}`}>Publication number <span className="text-slate-400">(optional)</span></Label>
                  <Input id={`pub-no-${item.id}`} placeholder="e.g. 28812345" value={item.report_number || ""} onChange={(e) => set("report_number", e.target.value)} data-testid={`ref-report-number-${index}`} />
                </div>
              </>
            )}
            {item.source_type === "conference" && (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`conftype-${item.id}`}>Conference item type</Label>
                  <Select value={item.conf_type || "paper"} onValueChange={(v) => set("conf_type", v)}>
                    <SelectTrigger id={`conftype-${item.id}`} data-testid={`ref-conf-type-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paper" data-testid={`conf-type-paper-${index}`}>Paper presentation</SelectItem>
                      <SelectItem value="poster" data-testid={`conf-type-poster-${index}`}>Poster presentation</SelectItem>
                      <SelectItem value="proceedings" data-testid={`conf-type-proceedings-${index}`}>Published proceedings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`conference-${item.id}`}>Conference name</Label>
                  <Input id={`conference-${item.id}`} placeholder="e.g. Annual Meeting of the American Psychological Association" value={item.conference || ""} onChange={(e) => set("conference", e.target.value)} data-testid={`ref-conference-${index}`} />
                </div>
                {item.conf_type !== "proceedings" && (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor={`eventdate-${item.id}`}>Conference date (month & day)</Label>
                      <Input id={`eventdate-${item.id}`} placeholder="e.g. March 5–7" value={item.event_date || ""} onChange={(e) => set("event_date", e.target.value)} data-testid={`ref-event-date-${index}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`location-${item.id}`}>Location</Label>
                      <Input id={`location-${item.id}`} placeholder="e.g. San Diego, CA, United States" value={item.location || ""} onChange={(e) => set("location", e.target.value)} data-testid={`ref-location-${index}`} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          </>
        )}

        <div className="mt-4 rounded-md bg-slate-50 p-3">
          <p className="text-xs font-medium text-slate-400">{style} preview</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700" data-testid={`ref-preview-${index}`}>
            {preview.parts.length ? (
              <CitationText {...preview} />
            ) : (
              <span className="italic text-slate-400">Fill in the fields above to see a formatted citation.</span>
            )}
          </p>
          {preview.parts.length ? (() => {
            if (isNumbered(style)) {
              const num = numberedInText(index + 1, style);
              return (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2">
                  <span className="text-xs font-medium text-slate-400">In-text:</span>
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => copyText(num, "Numbered citation copied.")} data-testid={`copy-numbered-btn-${index}`}>
                    <Copy className="h-3 w-3" /> {num}
                  </Button>
                </div>
              );
            }
            const it = inTextCitation(item, { suffix: yearSuffix });
            return (
              <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2">
                <span className="text-xs font-medium text-slate-400">In-text:</span>
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => copyText(it.parenthetical, "Parenthetical citation copied.")} data-testid={`copy-parenthetical-btn-${index}`}>
                  <Copy className="h-3 w-3" /> {it.parenthetical}
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => copyText(it.narrative, "Narrative citation copied.")} data-testid={`copy-narrative-btn-${index}`}>
                  <Copy className="h-3 w-3" /> {it.narrative}
                </Button>
              </div>
            );
          })() : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReferenceGenerator() {
  const { projectId } = useParams();
  const isEdit = Boolean(projectId);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [institution, setInstitution] = useState("");
  const [style, setStyle] = useState("APA");
  const [references, setReferences] = useState([newRef()]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [styledExport, setStyledExport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importName, setImportName] = useState("");
  const [importing, setImporting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRefs, setReviewRefs] = useState([]);
  const [reviewSectionFound, setReviewSectionFound] = useState(true);
  const [reviewSource, setReviewSource] = useState("");
  const [importSummary, setImportSummary] = useState(null);

  useEffect(() => {
    document.title = isEdit ? "Edit Reference Page — CiteQR" : "Create Reference Page — CiteQR";
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/projects/${projectId}`)
      .then(({ data }) => {
        setTitle(data.title);
        setAuthor(data.author_name || "");
        setInstitution(data.institution || "");
        setStyle(data.citation_style || "APA");
        setReferences(data.references?.length ? data.references.map((r) => ({ ...newRef(), ...r, source_type: r.source_type || resolveSourceType(r), authors_raw: r.authors_raw || r.authors || "", editors_raw: r.editors_raw || r.editors || "" })) : [newRef()]);
        if (data.status === "published") setSaved(data);
      })
      .catch((e) => { toast.error(errMessage(e)); navigate("/projects"); })
      .finally(() => setLoading(false));
  }, [isEdit, projectId, navigate]);

  const publicUrl = useMemo(
    () => (saved ? referencePageUrl(saved.public_id) : ""),
    [saved]
  );

  // Flag likely duplicates (same DOI, or same normalized title+year). Non-destructive: warn only.
  const duplicateGroups = useMemo(() => {
    const byKey = {};
    references.forEach((r, i) => {
      const doi = (r.doi || "").trim().toLowerCase()
        .replace(/^doi:\s*/i, "").replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
      const titleKey = (r.title || "").trim().toLowerCase().replace(/\s+/g, " ");
      const year = (r.year || "").trim();
      const key = doi ? `doi:${doi}` : (titleKey ? `t:${titleKey}|${year}` : "");
      if (!key) return;
      (byKey[key] = byKey[key] || []).push(i);
    });
    return Object.values(byKey).filter((idxs) => idxs.length > 1);
  }, [references]);

  const updateRef = (i, val) => setReferences((rs) => rs.map((r, idx) => (idx === i ? val : r)));
  const moveRef = (i, dir) => setReferences((rs) => {
    const copy = [...rs];
    const j = i + dir;
    if (j < 0 || j >= copy.length) return rs;
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const deleteRef = (i) => setReferences((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const [pendingDelete, setPendingDelete] = useState(null);
  const requestDeleteRef = (i) => { if (references.length === 1) return; setPendingDelete(i); };
  const confirmDeleteRef = () => { if (pendingDelete !== null) deleteRef(pendingDelete); setPendingDelete(null); };
  const addRef = () => setReferences((rs) => [...rs, newRef()]);

  const tidyAuthors = () => {
    let changed = 0;
    setReferences((rs) => rs.map((r) => {
      const next = { ...r };
      const aSrc = r.authors_raw || r.authors;
      if (aSrc) {
        const t = normalizeAuthors(aSrc, style);
        if (t && t !== r.authors) changed++;
        next.authors = t || r.authors;
        next.authors_raw = r.authors_raw || r.authors;
      }
      const eSrc = r.editors_raw || r.editors;
      if (eSrc) {
        const te = normalizeAuthors(eSrc, style);
        if (te && te !== r.editors) changed++;
        next.editors = te || r.editors;
        next.editors_raw = r.editors_raw || r.editors;
      }
      return next;
    }));
    toast.success(changed ? `Tidied ${changed} name list${changed > 1 ? "s" : ""} to ${style} style — please review.` : "Names already look tidy.");
  };

  const [pendingExport, setPendingExport] = useState(null);

  const runExport = (format) => {
    const clean = references.filter((r) => r.title || r.authors || r.doi);
    if (!clean.length) { toast.error("Add at least one reference to export."); return; }
    exportReferences(clean, style, format, styledExport);
    toast.success(`Exported ${clean.length} reference${clean.length > 1 ? "s" : ""} as ${format.toUpperCase()}${styledExport ? ` (${style} names)` : ""}.`);
    document.body.style.pointerEvents = "";
  };

  const MAX_DOCX_BYTES = 10 * 1024 * 1024; // 10 MB

  const openReview = (parsed, sectionFound, source = "Document") => {
    // Existing editor references (for cross-checking duplicates).
    const existingKeys = new Set(references.map((r) => dedupKey(r)).filter(Boolean));
    const seen = new Set();
    const rows = parsed.map((r) => {
      const key = dedupKey(r);
      const dupExisting = key && existingKeys.has(key);
      const dupWithin = key && seen.has(key);
      if (key) seen.add(key);
      return { ...newRef(), ...r, authors_raw: r.authors || "", _needsReview: !!r._needsReview, _duplicate: dupExisting || dupWithin, _selected: !(dupExisting || dupWithin) };
    });
    // Confidence sort: Needs review first, then possible duplicates, then the rest (stable).
    const rank = (r) => (r._needsReview ? 0 : r._duplicate ? 1 : 2);
    rows.forEach((r, i) => { r._ord = i; });
    rows.sort((a, b) => rank(a) - rank(b) || a._ord - b._ord);
    setReviewSource(source);
    setReviewRefs(rows);
    setReviewSectionFound(sectionFound);
    setReviewOpen(true);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name || "";
    const isDocx = /\.docx$/i.test(name) || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const isPdf = /\.pdf$/i.test(name) || file.type === "application/pdf";
    if (/\.doc$/i.test(name) && !isDocx) {
      toast.error("Legacy .doc files aren't supported yet — please save as .docx.");
      e.target.value = "";
      return;
    }
    if (isDocx || isPdf) {
      if (file.size > MAX_DOCX_BYTES) { toast.error(`That file is too large (max 10 MB).`); e.target.value = ""; return; }
      setImporting(true);
      try {
        const buf = await file.arrayBuffer();
        const { references: parsed, sectionFound } = isPdf
          ? parseReferenceText(await extractPdfText(buf))
          : await parseDocx(buf);
        if (!parsed.length) { toast.error(`No references were detected in this ${isPdf ? "PDF" : "document"}.`); return; }
        setImportOpen(false);
        openReview(parsed, sectionFound, isPdf ? "PDF (.pdf)" : "Word (.docx)");
      } catch (err) {
        toast.error(`Couldn't read this ${isPdf ? "PDF" : "Word"} file. It may be corrupted or not a valid ${isPdf ? ".pdf" : ".docx"}.`);
      } finally {
        setImporting(false);
        e.target.value = "";
      }
      return;
    }
    // Text formats (BibTeX / RIS / CSV) — unchanged behaviour.
    setImportName(name);
    const reader = new FileReader();
    reader.onload = () => setImportText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runImport = () => {
    if (!importText.trim()) { toast.error("Paste references or choose a file first."); return; }
    // Free-text paste (not BibTeX/RIS/CSV) -> detector + review screen.
    if (!isStructuredImport(importText, importName)) {
      const { references: parsed, sectionFound } = parseReferenceText(importText);
      if (!parsed.length) { toast.error("Couldn't detect any references in the pasted text."); return; }
      setImportOpen(false);
      setImportText("");
      setImportName("");
      openReview(parsed, sectionFound, "Pasted text");
      return;
    }
    const { format, refs } = parseReferences(importText, importName);
    if (!refs.length) { toast.error("Couldn't find any references. Check the file format (BibTeX, RIS, or CSV)."); return; }
    const imported = refs.map((r) => ({ ...newRef(), ...r, authors_raw: r.authors || "" }));
    const existingKeys = new Set(references.map((r) => dedupKey(r)).filter(Boolean));
    let dupCount = 0;
    const seen = new Set();
    imported.forEach((r) => {
      const key = dedupKey(r);
      if (key && (existingKeys.has(key) || seen.has(key))) dupCount++;
      if (key) seen.add(key);
    });
    setReferences((rs) => {
      const onlyEmpty = rs.length === 1 && !rs[0].title && !rs[0].authors && !rs[0].doi;
      return onlyEmpty ? imported : [...rs, ...imported];
    });
    setImportOpen(false);
    setImportText("");
    setImportName("");
    setImportSummary({ source: `${format.toUpperCase()} file`, imported: imported.length, duplicates: dupCount, needsReview: 0, skipped: 0 });
  };

  const setReviewField = (i, k, v) => setReviewRefs((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const toggleReviewSelect = (i) => setReviewRefs((rs) => rs.map((r, idx) => (idx === i ? { ...r, _selected: !r._selected } : r)));
  const deleteReviewRef = (i) => setReviewRefs((rs) => rs.filter((_, idx) => idx !== i));

  const confirmReviewImport = () => {
    const chosen = reviewRefs.filter((r) => r._selected);
    if (!chosen.length) { toast.error("Select at least one reference to import."); return; }
    const cleaned = chosen.map((r) => {
      const { _needsReview, _duplicate, _selected, _raw, _pmid, _ord, ...rest } = r;
      return { ...newRef(), ...rest, authors_raw: rest.authors_raw || rest.authors || "", editors_raw: rest.editors_raw || rest.editors || "" };
    });
    const skipped = reviewRefs.filter((r) => !r._selected);
    const summary = {
      source: reviewSource || "Document",
      imported: cleaned.length,
      duplicates: reviewRefs.filter((r) => r._duplicate).length,
      needsReview: chosen.filter((r) => r._needsReview).length,
      skipped: skipped.length,
    };
    setReferences((rs) => {
      const onlyEmpty = rs.length === 1 && !rs[0].title && !rs[0].authors && !rs[0].doi;
      return onlyEmpty ? cleaned : [...rs, ...cleaned];
    });
    setReviewOpen(false);
    setReviewRefs([]);
    setImportSummary(summary);
  };

  const [enriching, setEnriching] = useState(false);
  const enrichReviewDois = async () => {
    const targets = reviewRefs.map((r, i) => ({ i, doi: (r.doi || "").trim() })).filter((t) => t.doi);
    if (!targets.length) { toast.error("No DOIs found in the detected references."); return; }
    setEnriching(true);
    let ok = 0;
    const results = await Promise.allSettled(
      targets.map((t) => api.get("/lookup/doi", { params: { doi: t.doi } }).then(({ data }) => ({ i: t.i, data })))
    );
    setReviewRefs((rs) => {
      const next = rs.slice();
      for (const res of results) {
        if (res.status !== "fulfilled") continue;
        const { i, data } = res.value;
        ok++;
        next[i] = {
          ...next[i],
          authors: data.authors || next[i].authors,
          authors_raw: data.authors || next[i].authors_raw || next[i].authors,
          year: data.year || next[i].year,
          title: data.title || next[i].title,
          source: data.source || next[i].source,
          volume: data.volume || next[i].volume,
          issue: data.issue || next[i].issue,
          pages: data.pages || next[i].pages,
          publisher: data.publisher || next[i].publisher,
          url: data.url || next[i].url,
          _needsReview: false,
        };
      }
      return next;
    });
    setEnriching(false);
    toast[ok ? "success" : "error"](ok
      ? `Enriched ${ok} of ${targets.length} reference${targets.length > 1 ? "s" : ""} from Crossref. Please review.`
      : "Couldn't enrich any DOIs (they may be invalid or unavailable).");
  };

  const refIsBlank = (r) => !["authors", "authors_raw", "title", "source", "container", "editors", "publisher", "doi", "url"]
    .some((k) => (r[k] || "").trim());

  const persist = async (status) => {
    if (!title.trim()) { toast.error("Please enter a project or presentation title."); return; }
    // Blank Reference Guard: never save empty references (public pages must not silently skip entries).
    const nonEmpty = references.filter((r) => !refIsBlank(r));
    if (status === "published" && nonEmpty.length === 0) {
      toast.error("Add at least one reference with some detail before publishing.");
      return;
    }
    const dropped = references.length - nonEmpty.length;
    setSaving(true);
    const payload = {
      title, author_name: author, institution, citation_style: style,
      references: nonEmpty.map(({ id, ...r }) => ({ id, ...r })), status,
    };
    try {
      let data;
      if (isEdit) {
        ({ data } = await api.put(`/projects/${projectId}`, payload));
      } else {
        ({ data } = await api.post("/projects", payload));
      }
      // Reflect the guard result locally so the editor matches what was saved.
      if (data.references) {
        setReferences(data.references.length
          ? data.references.map((r) => ({ ...newRef(), ...r, source_type: r.source_type || resolveSourceType(r), authors_raw: r.authors_raw || r.authors || "", editors_raw: r.editors_raw || r.editors || "" }))
          : [newRef()]);
      }
      if (dropped > 0) toast.message(`${dropped} empty reference${dropped > 1 ? "s were" : " was"} skipped.`);
      if (status === "published") {
        setSaved(data);
        toast.success("Reference page published!");
      } else {
        toast.success("Draft saved.");
        if (!isEdit) navigate(`/reference/${data.id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => copyText(publicUrl, "Link copied.");

  const qrStyleTimer = useRef(null);
  const persistQrStyle = (style) => {
    if (!saved?.id) return;
    setSaved((s) => (s ? { ...s, qr_style: style } : s));
    if (qrStyleTimer.current) clearTimeout(qrStyleTimer.current);
    qrStyleTimer.current = setTimeout(() => {
      api.put(`/projects/${saved.id}/qr-style`, style)
        .then(({ data }) => setSaved(data))
        .catch(() => {});
    }, 600);
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 fade-in">
      <div className="max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
          {isEdit ? "Edit reference page" : "Create a reference page"}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          Build a clean, mobile-friendly public page for your citations, then generate a QR code linked to it.
        </p>
      </div>

      <Alert className="mt-6 border-slate-200 bg-slate-50">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-slate-600">
          Please review every formatted citation before publishing — automated formatting is a helpful starting point, not a guarantee of academic perfection.
          Do not include confidential, sensitive, or identifiable patient, participant, or student information.
        </AlertDescription>
      </Alert>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Editor */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-6">
              <h2 className="text-base font-semibold text-slate-900">Project details</h2>
              <div className="space-y-1.5">
                <Label htmlFor="proj-title">Project / presentation title <span className="text-red-500">*</span></Label>
                <Input id="proj-title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="project-title-input" placeholder="e.g., Effects of Sleep on Memory Consolidation" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="author">Author name</Label><Input id="author" value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="project-author-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="inst">Institution <span className="text-slate-400">(optional)</span></Label><Input id="inst" value={institution} onChange={(e) => setInstitution(e.target.value)} data-testid="project-institution-input" /></div>
              </div>
              <div className="space-y-1.5 sm:max-w-xs">
                <Label htmlFor="style">Citation style</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger id="style" data-testid="citation-style-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CITATION_STYLES.map((s) => <SelectItem key={s} value={s} data-testid={`style-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500" data-testid="style-order-note">
                  {["Vancouver", "IEEE"].includes(style)
                    ? `${style} lists references in numbered citation order (the order below).`
                    : `${style} references are automatically alphabetized by author on your published page.`}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">References ({references.length})</h2>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={tidyAuthors} data-testid="tidy-authors-btn"><Wand2 className="mr-2 h-4 w-4" /> Tidy names</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" data-testid="export-refs-btn"><Download className="mr-2 h-4 w-4" /> Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-64"
                  onCloseAutoFocus={(e) => {
                    if (pendingExport) {
                      e.preventDefault();
                      const f = pendingExport;
                      setPendingExport(null);
                      runExport(f);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <Label htmlFor="styled-export" className="text-xs font-normal text-slate-600">Format names in {style} style</Label>
                    <Switch id="styled-export" checked={styledExport} onCheckedChange={setStyledExport} data-testid="styled-export-toggle" />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setPendingExport("bibtex")} data-testid="export-bibtex">BibTeX (.bib)</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setPendingExport("ris")} data-testid="export-ris">RIS (.ris)</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setPendingExport("csv")} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button type="button" variant="outline" onClick={() => setImportOpen(true)} data-testid="import-refs-btn"><Upload className="mr-2 h-4 w-4" /> Import</Button>
              <Button type="button" variant="outline" onClick={addRef} data-testid="add-reference-btn"><Plus className="mr-2 h-4 w-4" /> Add reference</Button>
            </div>
          </div>

          <div className="space-y-4">
            {duplicateGroups.length > 0 && (
              <Alert className="border-amber-300 bg-amber-50" data-testid="duplicate-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Possible duplicate references</AlertTitle>
                <AlertDescription className="text-amber-700">
                  {duplicateGroups.map((g, i) => (
                    <div key={i} data-testid={`duplicate-group-${i}`}>
                      References {g.map((n) => n + 1).join(", ")} look like the same source.
                    </div>
                  ))}
                  <span className="mt-1 block text-xs">Review these before publishing — nothing is removed automatically.</span>
                </AlertDescription>
              </Alert>
            )}
            {(() => { const refSuffixes = yearSuffixMap(references); return references.map((r, i) => (
              <RefCard key={r.id} index={i} total={references.length} item={r} style={style} yearSuffix={refSuffixes.get(r)}
                onChange={(v) => updateRef(i, v)} onMove={moveRef} onDelete={requestDeleteRef} />
            )); })()}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => persist("draft")} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save draft
            </Button>
            <Button type="button" onClick={() => persist("published")} disabled={saving} className="bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid="publish-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} {saved ? "Update & republish" : "Publish page"}
            </Button>
          </div>
        </div>

        {/* Share panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-20 space-y-6">
            {saved ? (
              <Card className="border-slate-200 shadow-sm" data-testid="share-panel">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-base font-semibold text-slate-900">Your published page</h2>
                    <Badge variant="outline" className="shrink-0 border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="qr-unchanged-badge"><ShieldCheck className="mr-1 h-3.5 w-3.5" /> QR unchanged since publish</Badge>
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="flex-1 truncate text-xs text-slate-600" data-testid="public-url">{publicUrl}</span>
                    <Button size="icon" variant="ghost" onClick={copyLink} aria-label="Copy link" data-testid="copy-link-btn"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild variant="outline" size="sm" className="flex-1"><a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="view-page-btn"><ExternalLink className="mr-2 h-4 w-4" /> View</a></Button>
                    <Button asChild variant="outline" size="sm" className="flex-1"><a href={publicUrl} target="_blank" rel="noopener noreferrer"><Eye className="mr-2 h-4 w-4" /> Test</a></Button>
                    <ShareMenu url={publicUrl} title={title} prefix="editor-share" size="sm" className="flex-1" label="Share" />
                  </div>
                  <Separator />
                  <p className="text-xs text-slate-500">This QR code links to your page. Editing content later won't change the URL or code.</p>
                  <QRStudio fixedValue={referenceQrUrl(saved.public_id)} downloadName={`reference-${saved.public_id}`} compact initialStyle={saved.qr_style} onStyleChange={persistQrStyle} />
                  <Separator />
                  <Button asChild variant="outline" className="w-full border-[#0A2540] text-[#0A2540] hover:bg-slate-50">
                    <a href={`/poster/${saved.public_id}`} target="_blank" rel="noopener noreferrer" data-testid="poster-kit-btn">
                      <LayoutTemplate className="mr-2 h-4 w-4" /> Open Poster Kit
                    </a>
                  </Button>
                  <p className="text-xs text-slate-500">Get a one-tap high-res QR and a printable caption block sized for research posters.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed border-slate-300 bg-slate-50 shadow-none" data-testid="share-placeholder">
                <CardContent className="p-6 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white"><Send className="h-5 w-5 text-[#0D9488]" /></div>
                  <p className="text-sm font-medium text-slate-700">Publish to get your link & QR code</p>
                  <p className="mt-1 text-xs text-slate-500">Once published, a stable public URL and a downloadable QR code appear here.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import references</DialogTitle>
            <DialogDescription>
              Bring in references from <strong>Word (.docx)</strong>, <strong>PDF (.pdf)</strong>, <strong>BibTeX (.bib)</strong>, <strong>RIS (.ris)</strong>, or <strong>CSV (.csv)</strong>. The format is auto-detected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-4 text-sm font-medium text-slate-600 hover:bg-slate-50">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {importing ? "Reading document…" : (importName || "Choose a .docx, .pdf, .bib, .ris, or .csv file")}
              <input type="file" accept=".docx,.pdf,.bib,.ris,.csv,.txt,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={onImportFile} disabled={importing} data-testid="import-file-input" />
            </label>
            <p className="text-xs text-slate-500">Word & PDF files are read in your browser and never uploaded. Detected references open in a review screen before import.</p>
            <div className="flex items-center gap-3">
              <Separator className="flex-1" /><span className="text-xs text-slate-400">or paste</span><Separator className="flex-1" />
            </div>
            <Textarea
              rows={6}
              placeholder={"Paste BibTeX / RIS / CSV — or just paste a plain reference list and we'll detect each entry.\n\nCSV header example: authors,year,title,source,volume,issue,pages,doi,url"}
              value={importText}
              onChange={(e) => { setImportText(e.target.value); }}
              data-testid="import-textarea"
              className="font-mono text-xs"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={runImport} className="bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid="import-run-btn">
              <Upload className="mr-2 h-4 w-4" /> Import references
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import summary */}
      <Dialog open={!!importSummary} onOpenChange={(o) => { if (!o) setImportSummary(null); }}>
        <DialogContent className="max-w-md" data-testid="import-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0D9488]" /> Import complete</DialogTitle>
            <DialogDescription>
              Source detected: <strong data-testid="import-summary-source">{importSummary?.source}</strong>. Please review the added references before publishing.
            </DialogDescription>
          </DialogHeader>
          {importSummary && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="import-summary-imported">
                <div className="text-2xl font-bold text-emerald-700">{importSummary.imported}</div>
                <div className="mt-1 text-xs font-medium text-emerald-700">Added to editor</div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center" data-testid="import-summary-duplicates">
                <div className="text-2xl font-bold text-amber-700">{importSummary.duplicates}</div>
                <div className="mt-1 text-xs font-medium text-amber-700">Possible duplicates</div>
              </div>
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-center" data-testid="import-summary-needsreview">
                <div className="text-2xl font-bold text-orange-700">{importSummary.needsReview}</div>
                <div className="mt-1 text-xs font-medium text-orange-700">Flagged “needs review”</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center" data-testid="import-summary-skipped">
                <div className="text-2xl font-bold text-slate-600">{importSummary.skipped}</div>
                <div className="mt-1 text-xs font-medium text-slate-600">Skipped / not selected</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportSummary(null)} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="import-summary-close">Review references</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Review references (from Word .docx / PDF / paste) */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Review detected references</DialogTitle>
            <DialogDescription>
              {reviewRefs.length} reference{reviewRefs.length === 1 ? "" : "s"} detected{reviewSectionFound ? " from the document's reference section" : " (no reference section heading found — scanned the whole document)"}.
              Edit anything that looks off, deselect what you don't want, then import. CiteQR will re-format everything in {style} style.
            </DialogDescription>
          </DialogHeader>

          <div
            className="citeqr-review-scroll max-h-[60vh] overflow-y-auto overscroll-contain space-y-3 pr-2"
            style={{ scrollbarGutter: "stable" }}
            data-testid="review-list"
          >
            {(() => { const reviewSuffixes = yearSuffixMap(reviewRefs); return reviewRefs.map((r, i) => {
              const preview = formatReference(r, style, reviewSuffixes.get(r));
              return (
                <div key={r.id} className={`rounded-lg border p-3 ${r._selected ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/60"}`} data-testid={`review-item-${i}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox checked={r._selected} onCheckedChange={() => toggleReviewSelect(i)} className="mt-1" data-testid={`review-select-${i}`} aria-label="Include this reference" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500">Reference {i + 1}</span>
                        {r._needsReview && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" data-testid={`review-needs-${i}`}>Needs review</Badge>}
                        {r._duplicate && <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700" data-testid={`review-dup-${i}`}>Possible duplicate</Badge>}
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                        <div className="col-span-2 sm:col-span-6">
                          <Select value={r.source_type || "journal"} onValueChange={(v) => setReviewField(i, "source_type", v)}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`review-source-type-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`review-source-type-${s.value}-${i}`}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input className="col-span-2 sm:col-span-3 h-8 text-sm" placeholder="Author(s)" value={r.authors} onChange={(e) => setReviewField(i, "authors", e.target.value)} data-testid={`review-authors-${i}`} />
                        <Input className="h-8 text-sm" placeholder="Year" value={r.year} onChange={(e) => setReviewField(i, "year", e.target.value)} data-testid={`review-year-${i}`} />
                        <Input className="h-8 text-sm" placeholder="Month" value={r.month} onChange={(e) => setReviewField(i, "month", e.target.value)} data-testid={`review-month-${i}`} />
                        <Input className="h-8 text-sm" placeholder="Day" value={r.day} onChange={(e) => setReviewField(i, "day", e.target.value)} data-testid={`review-day-${i}`} />
                        <Input className="sm:col-span-6 h-8 text-sm" placeholder={r.source_type === "chapter" ? "Chapter title" : r.source_type === "book" ? "Book title" : r.source_type === "website" ? "Page title" : r.source_type === "report" ? "Report title" : r.source_type === "dissertation" ? "Dissertation / thesis title" : "Title"} value={r.title} onChange={(e) => setReviewField(i, "title", e.target.value)} data-testid={`review-title-${i}`} />

                        {/* Journal-specific */}
                        {r.source_type === "journal" && (<>
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Journal name" value={r.source} onChange={(e) => setReviewField(i, "source", e.target.value)} data-testid={`review-source-${i}`} />
                          <Input className="sm:col-span-2 h-8 text-sm" placeholder="Pages" value={r.pages} onChange={(e) => setReviewField(i, "pages", e.target.value)} data-testid={`review-pages-${i}`} />
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Volume" value={r.volume} onChange={(e) => setReviewField(i, "volume", e.target.value)} data-testid={`review-volume-${i}`} />
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Issue" value={r.issue} onChange={(e) => setReviewField(i, "issue", e.target.value)} data-testid={`review-issue-${i}`} />
                        </>)}

                        {/* Book-specific */}
                        {r.source_type === "book" && (
                          <Input className="sm:col-span-6 h-8 text-sm" placeholder="Publisher" value={r.publisher} onChange={(e) => setReviewField(i, "publisher", e.target.value)} data-testid={`review-publisher-${i}`} />
                        )}

                        {/* Chapter-specific */}
                        {r.source_type === "chapter" && (<>
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Editors (A. Editor; B. Editor)" value={r.editors} onChange={(e) => setReviewField(i, "editors", e.target.value)} data-testid={`review-editors-${i}`} />
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Book title" value={r.container} onChange={(e) => setReviewField(i, "container", e.target.value)} data-testid={`review-container-${i}`} />
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Chapter pages" value={r.pages} onChange={(e) => setReviewField(i, "pages", e.target.value)} data-testid={`review-pages-${i}`} />
                          <Input className="sm:col-span-3 h-8 text-sm" placeholder="Publisher" value={r.publisher} onChange={(e) => setReviewField(i, "publisher", e.target.value)} data-testid={`review-publisher-${i}`} />
                        </>)}

                        {/* Webpage-specific */}
                        {r.source_type === "website" && (
                          <Input className="sm:col-span-6 h-8 text-sm" placeholder="Website / publisher name" value={r.source} onChange={(e) => setReviewField(i, "source", e.target.value)} data-testid={`review-source-${i}`} />
                        )}

                        {/* Report-specific */}
                        {r.source_type === "report" && (<>
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Publisher / organization" value={r.publisher} onChange={(e) => setReviewField(i, "publisher", e.target.value)} data-testid={`review-publisher-${i}`} />
                          <Input className="sm:col-span-2 h-8 text-sm" placeholder="Report no." value={r.report_number || ""} onChange={(e) => setReviewField(i, "report_number", e.target.value)} data-testid={`review-report-number-${i}`} />
                        </>)}

                        {/* Dissertation / thesis-specific */}
                        {r.source_type === "dissertation" && (<>
                          <Select value={r.work_type || "doctoral"} onValueChange={(v) => setReviewField(i, "work_type", v)}>
                            <SelectTrigger className="sm:col-span-2 h-8 text-sm" data-testid={`review-work-type-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="doctoral" data-testid={`review-work-type-doctoral-${i}`}>Doctoral dissertation</SelectItem>
                              <SelectItem value="masters" data-testid={`review-work-type-masters-${i}`}>Master's thesis</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Institution (degree-granting)" value={r.institution || ""} onChange={(e) => setReviewField(i, "institution", e.target.value)} data-testid={`review-institution-${i}`} />
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Repository / database (if published)" value={r.source} onChange={(e) => setReviewField(i, "source", e.target.value)} data-testid={`review-source-${i}`} />
                          <Input className="sm:col-span-2 h-8 text-sm" placeholder="Publication no." value={r.report_number || ""} onChange={(e) => setReviewField(i, "report_number", e.target.value)} data-testid={`review-report-number-${i}`} />
                        </>)}

                        {/* Conference-specific */}
                        {r.source_type === "conference" && (<>
                          <Select value={r.conf_type || "paper"} onValueChange={(v) => setReviewField(i, "conf_type", v)}>
                            <SelectTrigger className="sm:col-span-2 h-8 text-sm" data-testid={`review-conf-type-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paper" data-testid={`review-conf-type-paper-${i}`}>Paper presentation</SelectItem>
                              <SelectItem value="poster" data-testid={`review-conf-type-poster-${i}`}>Poster presentation</SelectItem>
                              <SelectItem value="proceedings" data-testid={`review-conf-type-proceedings-${i}`}>Published proceedings</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Conference name" value={r.conference || ""} onChange={(e) => setReviewField(i, "conference", e.target.value)} data-testid={`review-conference-${i}`} />
                          <Input className="sm:col-span-4 h-8 text-sm" placeholder="Location (City, State, Country)" value={r.location || ""} onChange={(e) => setReviewField(i, "location", e.target.value)} data-testid={`review-location-${i}`} />
                          <Input className="sm:col-span-2 h-8 text-sm" placeholder="Event date (e.g. April 12)" value={r.event_date || ""} onChange={(e) => setReviewField(i, "event_date", e.target.value)} data-testid={`review-event-date-${i}`} />
                        </>)}

                        <Input className="sm:col-span-4 h-8 text-sm" placeholder="DOI" value={r.doi} onChange={(e) => setReviewField(i, "doi", e.target.value)} data-testid={`review-doi-${i}`} />
                        <Input className="sm:col-span-2 h-8 text-sm" placeholder="URL" value={r.url} onChange={(e) => setReviewField(i, "url", e.target.value)} data-testid={`review-url-${i}`} />
                      </div>
                      <div className="rounded bg-slate-50 px-2 py-1.5 text-xs leading-relaxed text-slate-600">
                        <span className="font-medium text-slate-400">{style} preview: </span>
                        {preview.parts.length ? <CitationText {...preview} /> : <span className="italic text-slate-400">Not enough detail yet.</span>}
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => deleteReviewRef(i)} aria-label="Delete detected reference" data-testid={`review-delete-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              );
            }); })()}
          </div>

          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs text-slate-500" data-testid="review-selected-count">{reviewRefs.filter((r) => r._selected).length} of {reviewRefs.length} selected</span>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={enrichReviewDois} disabled={enriching || !reviewRefs.some((r) => (r.doi || "").trim())} data-testid="review-enrich-btn">
                {enriching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Autofill DOIs ({reviewRefs.filter((r) => (r.doi || "").trim()).length})
              </Button>
              <Button variant="outline" onClick={() => { setReviewOpen(false); setReviewRefs([]); }} data-testid="review-cancel-btn">Cancel</Button>
              <Button onClick={confirmReviewImport} className="bg-[#0D9488] text-white hover:bg-[#0F766E]" data-testid="review-import-btn">
                <Upload className="mr-2 h-4 w-4" /> Import selected
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="delete-reference-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this reference?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-reference-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRef} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-reference-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
